/**
 * Öffnet ein Modal (.basket-modal) über Trigger-Elemente mit der Klasse
 * "modal-image-zoom", deren data-target-Attribut auf das Modal zeigt,
 * und stellt das Dialog-Verhalten barrierefrei bereit:
 * Fokus wandert beim Öffnen in den Dialog und beim Schließen zurück zum
 * Trigger, Escape schließt, Tab bleibt im Dialog (Fokusfalle).
 * Geschlossen wird über Elemente mit der Klasse "modal-zoom-close" im Modal
 * sowie über einen Klick auf den abgedunkelten Bereich neben dem Bild.
 *
 * Wichtig: Close-Buttons dieses Moduls dürfen NICHT die Legacy-Klasse
 * "m-close" tragen. Darauf hört der globale Handler in scripts/functions.js,
 * der das Modal ohne Wissen dieses Moduls schließt — der Fokus käme dann
 * nicht zum Trigger zurück und der interne Status bliebe stehen.
 *
 * initModalImageZoom() wird derzeit nur im checkoutStep1-Bundle aufgerufen,
 * also ausschließlich auf der Warenkorb-Seite. Wer ".modal-image-zoom" auf
 * einer anderen Seite einsetzt, muss den Aufruf dort ergänzen (oder ihn in
 * den globalen app-Entry in webpack.config.js verschieben) — sonst rendert
 * der Trigger, klickt aber ins Leere.
 *
 * Bilder im Modal können per data-src statt src ausgeliefert werden; sie
 * werden dann erst geladen, wenn der Trigger anvisiert oder das Modal
 * geöffnet wird. So kostet das Original nichts, solange niemand zoomt.
 */

import { getFocusableElements } from './focusable';

let openModal: HTMLElement | null = null;
let openTrigger: HTMLElement | null = null;
let initialized = false;

export function initModalImageZoom(): void {
    if (initialized) {
        return;
    }
    initialized = true;

    // Sobald der Trigger angefahren oder fokussiert wird, das Modal-Bild
    // vorladen — dann steht es beim Öffnen in der Regel schon bereit
    const warmUp = (event: Event) => {
        const modal = resolveModal(event.target);
        if (modal) {
            loadDeferredImages(modal);
        }
    };
    document.body.addEventListener('pointerover', warmUp);
    document.body.addEventListener('focusin', warmUp);

    document.body.addEventListener('click', event => {
        const target = event.target as HTMLElement;

        const trigger = target.closest<HTMLElement>('.modal-image-zoom');
        if (trigger) {
            const modal = resolveModal(trigger);
            if (modal) {
                event.preventDefault();
                open(modal, trigger);
            }
            return;
        }

        if (!openModal || !openModal.contains(target)) {
            return;
        }

        // Schließen über die Close-Buttons oder über den abgedunkelten Bereich
        // neben dem Bild (.basket-modal ist selbst der Backdrop)
        if (target.closest('.modal-zoom-close') || !target.closest('.modal-wrapper')) {
            close();
        }
    });

    document.addEventListener('keydown', event => {
        if (!openModal) {
            return;
        }

        if (event.key === 'Escape') {
            close();
        } else if (event.key === 'Tab') {
            trapFocus(event);
        }
    });
}

/**
 * Ermittelt aus einem Event-Ziel (oder direkt aus einem Trigger) das Modal,
 * auf das dessen data-target zeigt.
 */
function resolveModal(source: EventTarget | null): HTMLElement | null {
    if (!(source instanceof Element)) {
        return null;
    }

    const trigger = source.closest<HTMLElement>('.modal-image-zoom');
    const targetSelector = trigger ? trigger.getAttribute('data-target') : null;

    return targetSelector ? document.querySelector<HTMLElement>(targetSelector) : null;
}

/**
 * Setzt bei Bildern mit data-src das src und startet damit den Download.
 * Mehrfachaufrufe sind unschädlich, weil data-src danach entfernt ist.
 */
function loadDeferredImages(modal: HTMLElement): void {
    Array.from(modal.querySelectorAll<HTMLImageElement>('img[data-src]')).forEach(img => {
        const src = img.getAttribute('data-src');
        img.removeAttribute('data-src');

        if (src) {
            img.src = src;
        }
    });
}

function open(modal: HTMLElement, trigger: HTMLElement): void {
    openModal = modal;
    openTrigger = trigger;

    loadDeferredImages(modal);
    document.body.classList.add('modal-open');
    setLayoverVisible(true);
    modal.style.display = 'block';

    const firstFocusable = getFocusableElements(modal)[0];
    if (firstFocusable) {
        firstFocusable.focus();
    }
}

function close(): void {
    if (!openModal) {
        return;
    }

    openModal.style.display = 'none';

    // Backdrop und body.modal-open sind seitenweit geteilt (u. a. mit den
    // Legacy-Modals): erst zurücksetzen, wenn kein Modal mehr sichtbar ist
    if (!isAnyModalVisible()) {
        document.body.classList.remove('modal-open');
        setLayoverVisible(false);
    }

    if (openTrigger) {
        openTrigger.focus();
    }

    openModal = null;
    openTrigger = null;
}

/**
 * Prüft, ob noch irgendein Modal sichtbar ist. Bewusst über den berechneten
 * display-Wert, weil .basket-modal position: fixed ist und offsetParent
 * dort immer null liefert.
 */
function isAnyModalVisible(): boolean {
    return Array.from(document.querySelectorAll<HTMLElement>('.basket-modal')).some(
        modal => window.getComputedStyle(modal).display !== 'none'
    );
}

function setLayoverVisible(visible: boolean): void {
    const layover = document.getElementById('modal-bg-layover');
    if (layover) {
        layover.style.display = visible ? 'block' : 'none';
    }
}

function trapFocus(event: KeyboardEvent): void {
    if (!openModal) {
        return;
    }

    const focusable = getFocusableElements(openModal).filter(el => el.offsetParent !== null);
    if (focusable.length === 0) {
        return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;
    const activeInModal = active !== null && openModal.contains(active);

    if (event.shiftKey) {
        if (!activeInModal || active === first) {
            event.preventDefault();
            last.focus();
        }
    } else if (!activeInModal || active === last) {
        event.preventDefault();
        first.focus();
    }
}
